import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle, CheckCircle2, ShieldAlert, ArrowRight, ChevronLeft, ExternalLink,
  Sparkles, Loader2, Lock, ShieldCheck, Eye, EyeOff, Database, FileWarning,
} from "lucide-react";
import api from "@/lib/api";
import PrivacyScoreDial from "@/components/PrivacyScoreDial";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ScanResults() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [deepLoading, setDeepLoading] = useState(false);

  useEffect(() => {
    const cached = sessionStorage.getItem("lastScan");
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.scan_id === scanId) {
        setScan(parsed);
        return;
      }
    }
    api.get(`/scan/${scanId}`).then((r) => setScan(r.data)).catch(() => {
      toast.error("No se pudo cargar el escaneo");
    });
  }, [scanId]);

  const runDeepScan = async () => {
    setDeepLoading(true);
    try {
      const { data } = await api.post(`/scan/${scanId}/deep`, null, { timeout: 90000 });
      setScan(data);
      sessionStorage.setItem("lastScan", JSON.stringify(data));
      toast.success("Escaneo profundo (Playwright) completado — capturas guardadas");
    } catch (e) {
      toast.error("Error al ejecutar escaneo profundo");
    } finally {
      setDeepLoading(false);
    }
  };

  if (!scan) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 font-mono text-sm text-gray-500">
        Cargando escaneo…
      </div>
    );
  }

  // Teaser mode (anonymous / unsubscribed viewer): show v3 redacted UI.
  if (scan.teaser) {
    return <TeaserView scan={scan} navigate={navigate} />;
  }

  // Full mode (subscribed viewer) — keep the previous detailed layout.
  return <FullView scan={scan} navigate={navigate} deepLoading={deepLoading} runDeepScan={runDeepScan} />;
}

// ---------------------------------------------------------------------------
// SCAN RESULTS V3 — anonymous teaser
// ---------------------------------------------------------------------------

function TeaserView({ scan, navigate }) {
  const totals = scan.totals || {};
  const exposed = totals.exposed || 0;
  const findings = scan.findings || [];
  // Show all eliminable brokers (non-Tier 3) as locked cards
  const eliminable = findings.filter((f) => f.tier !== 3);
  const gender = scan.gender || "unknown";
  const avatarSrc = gender === "female"
    ? "/avatars/female.png"
    : gender === "male"
      ? "/avatars/male.png"
      : "/avatars/male.png"; // safe default

  return (
    <div className="bg-[#F9F9F8] min-h-screen">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <button
          onClick={() => navigate("/")}
          data-testid="scan-back"
          className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>

        {/* HEADER ROW: identity card + headline */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Avatar identity card */}
          <motion.div
            initial={{ y: 12 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-4 bg-white border border-gray-200 overflow-hidden shadow-[0_4px_0_0_rgba(15,23,42,0.04)]"
            data-testid="teaser-identity-card"
          >
            <div className="relative aspect-square bg-[#E9E2D4] overflow-hidden">
              <motion.img
                src={avatarSrc}
                alt="Avatar"
                className="w-full h-full object-cover"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7 }}
              />
              <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 text-[10px] font-mono uppercase tracking-[0.15em] text-gray-700 border border-gray-200 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Datos enmascarados
              </div>
            </div>
            <div className="p-5">
              <div className="eyebrow mb-3">Tu perfil expuesto</div>
              <RevealRow label="Nombre" value={scan.masked_nombre} delay={0.2} />
              <RevealRow label="RUT" value={scan.masked_rut} delay={0.3} mono />
              {scan.has_email && (
                <RevealRow label="Email" value={scan.masked_email} delay={0.35} mono />
              )}
              {scan.has_phone && (
                <RevealRow label="Teléfono" value={scan.masked_phone} delay={0.4} mono />
              )}
              <RevealRow
                label="Sexo"
                value={gender === "female" ? "Femenino" : gender === "male" ? "Masculino" : "—"}
                delay={0.45}
              />
              <div className="mt-5 pt-4 border-t border-dashed border-gray-200">
                <div className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
                  <EyeOff className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                  <span>
                    Ocultamos tu nombre, RUT, email y teléfono hasta que te
                    suscribas. Mostramos los <strong>sitios donde apareces</strong>{" "}
                    para que veas el alcance — los datos exactos los desbloqueas dentro.
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Headline + counters + CTA */}
          <div className="lg:col-span-8">
            <motion.div initial={{ y: 8 }} animate={{ y: 0 }} transition={{ duration: 0.4 }}>
              <div className="eyebrow mb-3 text-red-600">Escaneo real · {new Date(scan.created_at).toLocaleString("es-CL")}</div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] text-gray-900">
                Tu RUT está expuesto en{" "}
                <CounterNumber to={exposed} />{" "}
                <span className="text-red-600">brokers chilenos</span>.
              </h1>
              <p className="mt-4 text-gray-700 leading-relaxed max-w-2xl">
                Escaneamos <strong>{totals.scanned}</strong> bases de datos en
                paralelo. {totals.confirmed > 0 && (
                  <span className="text-red-700">
                    <strong>{totals.confirmed}</strong> con coincidencia textual confirmada.
                  </span>
                )}{" "}
                {totals.blocked > 0 && (
                  <span className="text-amber-700">
                    <strong>{totals.blocked}</strong> bloquean nuestros scrapers — el sitio
                    está vivo pero requiere acción legal directa.
                  </span>
                )}
              </p>
            </motion.div>

            {/* Counter grid */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Tier 1 — Rutificadores" value={totals.tier1} variant="danger" delay={0.2} />
              <Stat label="Tier 2 — Bases masivas" value={totals.tier2} variant="warning" delay={0.3} />
              <Stat label="Datos públicos" value={totals.tier3} variant="info" delay={0.4} />
              <Stat label="Puntaje privacidad" value={scan.score} variant="dark" delay={0.5} mono />
            </div>

            {/* Score + CTA */}
            <motion.div
              initial={{ y: 8 }}
              animate={{ y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="mt-7 bg-white border border-gray-200 p-6 flex flex-col sm:flex-row gap-6 items-center"
            >
              <PrivacyScoreDial score={scan.score} size={140} />
              <div className="flex-1 w-full">
                <div className="eyebrow mb-2">Al suscribirte desbloqueas</div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <Bullet>El <strong>detalle exacto de los datos</strong> que cada sitio publica de ti (nombre, RUT, email, teléfono, dirección).</Bullet>
                  <Bullet>Solicitudes ARCOP firmadas y enviadas automáticamente a los {exposed} brokers.</Bullet>
                  <Bullet>Estadísticas y seguimiento de cada eliminación en tiempo real.</Bullet>
                  <Bullet>Escalada legal a la APDP si un responsable no responde en 30 días (art. 15 Ley 21.719).</Bullet>
                </ul>
                <button
                  onClick={() => {
                    track("teaser_cta_click", { position: "primary", scan_id: scan.scan_id, exposed: exposed });
                    navigate(`/register?from=scan&scan_id=${scan.scan_id}`);
                  }}
                  data-testid="teaser-cta-unlock"
                  className="mt-5 w-full bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-4 px-6 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5"
                >
                  Desbloquear y eliminar mis datos · $39.990 <ArrowRight className="w-4 h-4" />
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center font-mono">
                  Pago único · Sin renovaciones · Webpay Transbank
                </p>
              </div>
            </motion.div>

            {/* Quota notice */}
            {typeof scan.scans_remaining === "number" && (
              <div
                className={`mt-4 text-xs font-mono flex items-center gap-2 ${
                  scan.scans_remaining <= 0 ? "text-red-700" : "text-gray-500"
                }`}
                data-testid="teaser-quota"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                {scan.scans_remaining > 0
                  ? `Te queda ${scan.scans_remaining} escaneo${scan.scans_remaining === 1 ? "" : "s"} gratuito${scan.scans_remaining === 1 ? "" : "s"} en las próximas 24h. Crea tu cuenta para escaneos ilimitados.`
                  : "Ya usaste tus 2 escaneos gratuitos. Crea tu cuenta para continuar."}
              </div>
            )}
          </div>
        </div>

        {/* LOCKED BROKER GRID */}
        <div className="mt-14">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="eyebrow text-red-600">Sitios donde está expuesto tu RUT</div>
              <h2 className="font-display text-2xl font-bold text-gray-900 mt-2">
                {eliminable.length} brokers chilenos publican tus datos
              </h2>
            </div>
            <div className="text-xs font-mono text-gray-500">
              Desbloquea con $39.990 (pago único) para ver qué dato exacto tiene cada uno
            </div>
          </div>

          {/* Data-types exposure banner — labels only, never values */}
          <ExposedFieldsBanner
            totalFields={totals.exposed_fields || 0}
            fieldCounts={scan.exposed_field_counts || {}}
            onCta={() => {
              track("teaser_cta_click", { position: "fields_banner", scan_id: scan.scan_id });
              navigate(`/register?from=scan&scan_id=${scan.scan_id}`);
            }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {eliminable.map((f, idx) => (
              <LockedBrokerCard key={idx} f={f} idx={idx} />
            ))}
          </div>

          {/* BLOQUE B — Empresas (presunción, sin evidencia) */}
          <EmpresasBlockB />

          {/* Bottom CTA echo */}
          <motion.div
            initial={{ y: 8 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-10 bg-[#0B2447] text-white p-8 sm:p-10 text-center"
            data-testid="teaser-cta-bottom"
          >
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-amber-300" />
            <div className="font-display text-2xl sm:text-3xl font-bold leading-tight">
              Te mostramos <em className="not-italic text-amber-300">dónde</em>.{" "}
              Suscríbete para ver <em className="not-italic text-amber-300">qué datos</em> y eliminarlos.
            </div>
            <p className="mt-3 text-white/80 max-w-2xl mx-auto leading-relaxed">
              Al suscribirte accedes a: el detalle de los datos personales expuestos en cada
              sitio (nombre completo, RUT, dirección, email, teléfono y más), la generación
              automática de tus solicitudes ARCOP, el envío certificado, las estadísticas y
              seguimiento de cada eliminación, y la escalada legal a la APDP si un responsable no
              responde en 30 días.
            </p>
            <button
              onClick={() => {
                track("teaser_cta_click", { position: "bottom", scan_id: scan.scan_id });
                navigate(`/register?from=scan&scan_id=${scan.scan_id}`);
              }}
              data-testid="teaser-cta-unlock-bottom"
              className="mt-6 inline-flex items-center gap-2 bg-white text-[#0B2447] hover:bg-amber-300 transition-colors font-semibold px-7 py-3.5"
            >
              Empezar por $39.990 <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-3 text-[11px] font-mono text-white/60">
              Pago único · Sin renovaciones · Webpay Transbank
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function RevealRow({ label, value, delay = 0, mono }) {
  return (
    <motion.div
      initial={{ y: 4 }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="flex items-baseline justify-between gap-3 py-1.5"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">{label}</span>
      <span className={`text-gray-900 ${mono ? "font-mono" : ""} text-sm`}>{value || "—"}</span>
    </motion.div>
  );
}

function CounterNumber({ to }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let start = 0;
    const dur = 900;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min(1, (Date.now() - t0) / dur);
      setN(Math.round(start + (to - start) * easeOut(p)));
      if (p < 1) requestAnimationFrame(tick);
    };
    tick();
  }, [to]);
  return <span className="text-red-600 tabular-nums" data-testid="teaser-exposed-count">{n}</span>;
}

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

function Stat({ label, value, variant = "info", delay = 0, mono }) {
  const styles = {
    danger: "bg-red-50 border-red-200 text-red-700",
    warning: "bg-amber-50 border-amber-200 text-amber-700",
    info: "bg-gray-50 border-gray-200 text-gray-700",
    dark: "bg-[#0B2447] border-[#0B2447] text-white",
  }[variant];
  return (
    <motion.div
      initial={{ y: 6 }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className={`border p-4 ${styles}`}
    >
      <div className={`text-[10px] font-bold uppercase tracking-[0.12em] ${variant === "dark" ? "text-amber-300" : "opacity-70"}`}>
        {label}
      </div>
      <div className={`mt-1 text-2xl font-display font-bold ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </motion.div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

const STATUS_PILL = {
  confirmed: { label: "Coincidencia confirmada", cls: "bg-red-600 text-white" },
  exposed: { label: "Exposición probable", cls: "bg-orange-500 text-white" },
  blocked: { label: "Bloqueado · acción legal", cls: "bg-amber-500 text-white" },
  clean: { label: "Sin coincidencias", cls: "bg-green-600 text-white" },
  unreachable: { label: "Sitio caído", cls: "bg-gray-400 text-white" },
  error: { label: "Error técnico", cls: "bg-gray-400 text-white" },
  public_record: { label: "Público · no eliminable", cls: "bg-gray-300 text-gray-900" },
};

function LockedBrokerCard({ f, idx }) {
  const pill = STATUS_PILL[f.scan_status] || STATUS_PILL.error;
  const isHit = !!f.hit;
  const exposedLabels = f.evidence?.exposed_fields || [];
  return (
    <motion.div
      initial={{ y: 8 }}
      animate={{ y: 0 }}
      transition={{ delay: 0.04 * Math.min(idx, 12), duration: 0.3 }}
      whileHover={{ y: -2 }}
      className={`relative border bg-white p-5 transition-shadow hover:shadow-[0_8px_0_-4px_rgba(11,36,71,0.08)] ${
        isHit ? "border-red-200" : "border-gray-200"
      }`}
      data-testid={`teaser-broker-${idx}`}
    >
      {/* Tier badge */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-400">
          Tier {f.tier} · {f.anon_label?.split("·")[1]?.trim() || `#${String(idx + 1).padStart(2, "0")}`}
        </span>
        <Database className={`w-3.5 h-3.5 ${isHit ? "text-red-500" : "text-gray-300"}`} />
      </div>

      {/* Broker identity — VISIBLE (this is public knowledge anyway) */}
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 border flex items-center justify-center shrink-0 ${
          isHit ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
        }`}>
          <Database className={`w-4 h-4 ${isHit ? "text-red-500" : "text-gray-400"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm text-gray-900 truncate" data-testid={`teaser-broker-name-${idx}`}>
            {f.broker_name}
          </div>
          <div className="text-[10px] font-mono text-gray-400 mt-0.5">
            {exposedLabels.length > 0
              ? `${exposedLabels.length} campo${exposedLabels.length === 1 ? "" : "s"} personal${exposedLabels.length === 1 ? "" : "es"} publicado${exposedLabels.length === 1 ? "" : "s"}`
              : f.data_types_count > 0
              ? `${f.data_types_count} tipo${f.data_types_count === 1 ? "" : "s"} de dato`
              : "sitio detectado"}
          </div>
        </div>
      </div>

      {/* Exposed field type pills — labels only, never values */}
      {exposedLabels.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1" data-testid={`teaser-fields-${idx}`}>
          {exposedLabels.slice(0, 6).map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-red-50 border border-red-200 text-red-700 px-1.5 py-0.5"
            >
              <Lock className="w-2.5 h-2.5" /> {label}
            </span>
          ))}
          {exposedLabels.length > 6 && (
            <span className="text-[10px] font-mono text-gray-400 px-1">
              +{exposedLabels.length - 6}
            </span>
          )}
        </div>
      )}

      {/* Redacted-value hint — the message the user asked for */}
      {isHit && exposedLabels.length > 0 && (
        <div className="mt-3 bg-gray-50 border-l-2 border-gray-300 pl-3 py-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-gray-500 flex items-center gap-1">
            <EyeOff className="w-3 h-3" /> Valores ocultos por seguridad
          </div>
          <div className="mt-1 text-[11px] text-gray-600 leading-snug">
            No podemos mostrarte los valores exactos aquí. <strong className="text-gray-900">
            Desbloquéalos con el plan único ($39.990)</strong> para verlos y
            enviar la solicitud ARCOP de eliminación.
          </div>
        </div>
      )}

      {/* Status pill */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 ${pill.cls}`}>
          {pill.label}
        </span>
        {f.evidence?.has_screenshot && (
          <span className="text-[10px] font-mono uppercase text-gray-400 inline-flex items-center gap-1">
            <FileWarning className="w-3 h-3" /> evidencia
          </span>
        )}
      </div>
    </motion.div>
  );
}

// Removed: generateBlurName — broker identity is now visible in every card.

// ---------------------------------------------------------------------------
// FULL VIEW (subscribed user) — kept structurally similar to the previous page
// ---------------------------------------------------------------------------

function FullView({ scan, navigate, deepLoading, runDeepScan }) {
  const tier1 = scan.findings.filter((f) => f.tier === 1);
  const tier2 = scan.findings.filter((f) => f.tier === 2);
  const tier3 = scan.findings.filter((f) => f.tier === 3);
  const confirmed = scan.findings.filter((f) => f.evidence?.status === "confirmed").length;
  const exposedCount = scan.findings.filter((f) => f.evidence?.status === "exposed").length;
  const blockedCount = scan.findings.filter((f) => f.evidence?.status === "blocked").length;
  const totalExposed = confirmed + exposedCount + blockedCount;

  return (
    <div className="bg-[#F9F9F8] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <button onClick={() => navigate("/")} data-testid="scan-back" className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-8">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Resultados del escaneo real</div>
            <h1 className="font-display text-3xl lg:text-5xl font-bold leading-tight text-gray-900">
              {(scan.nombre || "").split(" ")[0]}, tu RUT figura expuesto en{" "}
              <span className="text-red-600">{totalExposed} brokers</span>.
            </h1>
            <p className="mt-4 text-gray-700 leading-relaxed">
              RUT <span className="font-mono">{scan.rut}</span> · Escaneamos {scan.findings.length} fuentes en paralelo con peticiones HTTP reales.
              {confirmed > 0 && <span className="block mt-2 text-red-700"><strong>{confirmed}</strong> con coincidencia textual confirmada.</span>}
              {blockedCount > 0 && <span className="block text-amber-700"><strong>{blockedCount}</strong> bloquea scrapers — el sitio está vivo pero requiere acción legal directa.</span>}
            </p>

            <div className="mt-10 flex items-center gap-8 bg-white border border-gray-300 p-6">
              <PrivacyScoreDial score={scan.score} size={160} />
              <div>
                <div className="eyebrow mb-2">Tu score actual</div>
                <p className="text-sm text-gray-700 max-w-xs leading-relaxed">
                  Tu puntaje sube automáticamente a medida que confirmamos eliminaciones con cada broker.
                </p>
              </div>
            </div>

            <div className="mt-6 border border-dashed border-gray-300 bg-white p-5">
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-[#0B2447] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-display font-semibold text-sm text-gray-900">Escaneo profundo con navegador real</div>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Renderizamos JavaScript de los 4 rutificadores principales, simulamos la búsqueda y capturamos una <span className="font-semibold">screenshot legal</span> como evidencia ARCOP. Tarda ~10 segundos.
                  </p>
                  <button
                    onClick={runDeepScan}
                    disabled={deepLoading}
                    data-testid="scan-deep-button"
                    className="mt-3 w-full border border-[#0B2447] text-[#0B2447] hover:bg-[#0B2447] hover:text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {deepLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Renderizando…</> : <>Ejecutar escaneo profundo <Sparkles className="w-3.5 h-3.5" /></>}
                  </button>
                  {scan.deep_scanned_at && (
                    <div className="mt-2 text-[10px] font-mono text-green-700">
                      ✓ Último escaneo profundo: {new Date(scan.deep_scanned_at).toLocaleString("es-CL")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <Section title={`Tier 1 — Rutificadores (${tier1.length})`} variant="danger" testid="scan-tier1">
              {tier1.map((f) => <BrokerRow key={f.slug} f={f} />)}
            </Section>
            <Section title={`Tier 2 — Bases de datos masivas (${tier2.length})`} variant="warning" testid="scan-tier2">
              {tier2.map((f) => <BrokerRow key={f.slug} f={f} />)}
            </Section>
            <Section title={`Tier 3 — Datos públicos del Estado (${tier3.length})`} variant="info" testid="scan-tier3">
              {tier3.map((f) => <BrokerRow key={f.slug} f={f} isPublic />)}
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, variant, children, testid }) {
  const colors = {
    danger: "border-red-300",
    warning: "border-amber-300",
    success: "border-green-300",
    info: "border-gray-300",
  };
  return (
    <div className={`bg-white border ${colors[variant]}`} data-testid={testid}>
      <div className="px-5 py-3 border-b border-gray-200 eyebrow flex items-center justify-between">
        <span>{title}</span>
      </div>
      <div className="divide-y divide-gray-100">{children}</div>
    </div>
  );
}

const STATUS_UI = {
  confirmed:     { color: "text-red-600",    bg: "bg-red-50",     label: "Coincidencia confirmada" },
  exposed:       { color: "text-orange-600", bg: "bg-orange-50",  label: "Exposición probable" },
  blocked:       { color: "text-amber-600",  bg: "bg-amber-50",   label: "Bloqueado por Cloudflare" },
  clean:         { color: "text-green-600",  bg: "bg-green-50",   label: "Sin coincidencias" },
  unreachable:   { color: "text-gray-500",   bg: "bg-gray-50",    label: "Sitio caído" },
  error:         { color: "text-gray-500",   bg: "bg-gray-50",    label: "Error técnico" },
  public_record: { color: "text-gray-500",   bg: "bg-gray-50",    label: "Público — no eliminable" },
};

function BrokerRow({ f, isPublic }) {
  const ev = f.evidence || {};
  const ui = STATUS_UI[ev.status] || STATUS_UI.error;
  const isPw = ev.method_used === "playwright" || (ev.notes || "").includes("[Playwright]");
  const captureMatch = (ev.notes || "").match(/Captura:\s*([\w.\-]+\.png)/);
  const screenshotUrl = captureMatch ? `${API_BASE}/api/evidence/${captureMatch[1]}` : null;
  const extracted = ev.extracted_data;
  return (
    <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="px-5 py-4" data-testid={`scan-row-${f.slug}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <ShieldAlert className={`w-4 h-4 mt-0.5 ${ui.color}`} />
          <div className="min-w-0">
            <div className="font-mono text-sm text-gray-900 flex items-center gap-2 flex-wrap">
              <span>{f.name}</span>
              {ev.http_status != null && !isPublic && (
                <span className="text-[10px] font-mono text-gray-400">HTTP {ev.http_status}{ev.response_time_ms ? ` · ${ev.response_time_ms}ms` : ""}</span>
              )}
              {isPw && <span className="text-[10px] font-mono uppercase tracking-[0.15em] bg-[#0B2447] text-white px-1.5 py-0.5">Playwright</span>}
              {extracted && <span className="text-[10px] font-mono uppercase tracking-[0.15em] bg-red-600 text-white px-1.5 py-0.5">Datos extraídos</span>}
              {!extracted && ev.exposed_fields && <span className="text-[10px] font-mono uppercase tracking-[0.15em] bg-red-600 text-white px-1.5 py-0.5">Datos expuestos</span>}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{(f.data_types || []).join(" · ")}</div>
            {ev.notes && !isPublic && <div className="text-xs text-gray-500 mt-1 italic">{ev.notes}</div>}
            {ev.url && !isPublic && (
              <a href={ev.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-blue-600 hover:underline mt-1 inline-flex items-center gap-1">
                Ver evidencia <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${ui.color} shrink-0 ${ui.bg} px-2 py-1`}>{ui.label}</span>
      </div>

      {(extracted || ev.exposed_fields) && (
        <div className="mt-3 ml-7 bg-red-50 border-l-2 border-red-500 p-4" data-testid={`extracted-${f.slug}`}>
          <div className="eyebrow text-red-700 mb-2">
            {extracted ? `Datos personales expuestos en ${f.name}` : `Campos expuestos en ${f.name}`}
          </div>
          {extracted ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {extracted.nombre && <KVPair k="Nombre" v={extracted.nombre} />}
              {extracted.rut && <KVPair k="RUT" v={extracted.rut} mono />}
              {extracted.email && <KVPair k="Email" v={extracted.email} mono />}
              {extracted.telefono && <KVPair k="Teléfono" v={extracted.telefono} mono />}
              {extracted.direccion && <KVPair k="Dirección" v={extracted.direccion} />}
              {extracted.comuna && <KVPair k="Comuna" v={extracted.comuna} />}
              {extracted.region && <KVPair k="Región" v={extracted.region} />}
              {extracted.sexo && <KVPair k="Sexo" v={extracted.sexo} />}
              {extracted.edad && <KVPair k="Edad" v={extracted.edad} />}
              {extracted.profesion && <KVPair k="Profesión" v={extracted.profesion} />}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ev.exposed_fields.map((label) => (
                  <span key={label} className="text-[10px] uppercase tracking-[0.12em] font-bold bg-white border border-red-300 text-red-700 px-2 py-1">
                    {label}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-xs text-red-700/80 italic">
                Suscríbete para ver exactamente qué datos tuyos están publicados y eliminarlos.
              </div>
            </>
          )}
        </div>
      )}

      {screenshotUrl && (
        <a href={screenshotUrl} target="_blank" rel="noreferrer" className="block mt-3 ml-7 border border-gray-200 hover:border-[#0B2447] transition-colors">
          <img src={screenshotUrl} alt="Evidencia visual" className="w-full max-w-md max-h-40 object-cover object-top" loading="lazy" />
          <div className="text-[10px] font-mono text-gray-500 px-2 py-1 bg-gray-50">Screenshot legal capturado · clic para ampliar</div>
        </a>
      )}
    </motion.div>
  );
}

function KVPair({ k, v, mono }) {
  return (
    <div className="flex gap-2 items-baseline">
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-red-700/70 shrink-0 w-16">{k}</span>
      <span className={`text-gray-900 ${mono ? "font-mono" : ""} break-all`}>{v}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCAN RESULTS V3 — Bloque B: Empresas (presunción, sin scraping)
// ---------------------------------------------------------------------------

function ExposedFieldsBanner({ totalFields, fieldCounts, onCta }) {
  // Show the field types (labels only) with an explicit "we won't show values" msg.
  const entries = Object.entries(fieldCounts || {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  if (totalFields <= 0 || entries.length === 0) {
    // Fallback banner when no extraction happened yet — still warn the visitor.
    return (
      <div
        className="mb-6 bg-gray-900 text-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
        data-testid="exposed-fields-banner"
      >
        <div className="w-10 h-10 shrink-0 bg-white/10 border border-white/20 flex items-center justify-center">
          <Lock className="w-5 h-5 text-amber-300" />
        </div>
        <div className="flex-1">
          <div className="eyebrow text-amber-300 mb-1">Datos personales enmascarados</div>
          <p className="text-sm text-white/80 leading-relaxed">
            Detectamos tu RUT publicado en estos sitios. <strong className="text-white">
            No podemos mostrarte los valores exactos</strong> — solo los verás al
            desbloquear el plan único ($39.990) para eliminarlos.
          </p>
        </div>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="mb-6 bg-gray-900 text-white p-6 sm:p-7"
      data-testid="exposed-fields-banner"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 shrink-0 bg-white/10 border border-white/20 flex items-center justify-center">
          <Lock className="w-5 h-5 text-amber-300" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="eyebrow text-amber-300 mb-1">Datos personales enmascarados</div>
          <div className="font-display text-xl sm:text-2xl font-bold leading-snug">
            Detectamos <span className="text-amber-300 tabular-nums">{totalFields}</span>{" "}
            campo{totalFields === 1 ? "" : "s"} personal{totalFields === 1 ? "" : "es"} expuesto{totalFields === 1 ? "" : "s"} en total
          </div>
          <p className="mt-2 text-sm text-white/80 leading-relaxed max-w-2xl">
            <ShieldAlert className="inline w-4 h-4 text-amber-300 mr-1 -mt-0.5" />
            <strong className="text-white">Por seguridad no podemos mostrarte los valores.</strong>{" "}
            Solo los verás al pagar el plan único ($39.990) para eliminarlos con
            solicitudes ARCOP firmadas.
          </p>
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="exposed-fields-list">
            {entries.map(([label, n]) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-mono px-2 py-1"
              >
                <Lock className="w-3 h-3 text-amber-300" />
                <span className="uppercase tracking-[0.1em] font-bold">{label}</span>
                <span className="text-amber-300 tabular-nums">×{n}</span>
              </span>
            ))}
          </div>
        </div>
        <button
          onClick={onCta}
          data-testid="exposed-fields-cta"
          className="shrink-0 bg-amber-300 hover:bg-amber-200 text-gray-900 font-bold px-5 py-3 inline-flex items-center gap-2 transition-colors"
        >
          Desbloquear · $39.990 <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}


function EmpresasBlockB() {
  const [empresas, setEmpresas] = useState([]);

  useEffect(() => {
    api.get("/brokers?entity_type=empresa")
      .then((r) => setEmpresas(r.data || []))
      .catch(() => setEmpresas([]));
  }, []);

  if (empresas.length === 0) return null;

  const byCat = {};
  empresas.forEach((e) => {
    const k = e.category || "Otros";
    (byCat[k] = byCat[k] || []).push(e);
  });
  Object.values(byCat).forEach((arr) =>
    arr.sort((a, b) => (b.spam_score || 0) - (a.spam_score || 0))
  );
  const cats = Object.keys(byCat).sort();

  return (
    <div className="mt-16" data-testid="empresas-block-b">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-amber-700">Empresas que probablemente tienen tus datos</div>
          <h2 className="font-display text-2xl font-bold text-gray-900 mt-2">
            Bancos, telcos, retail: el spam del día a día
          </h2>
          <p className="mt-2 text-sm text-gray-600 max-w-2xl">
            No las escaneamos — no exponen los datos públicamente. Pero si tienes cuenta bancaria, plan
            móvil o has comprado en retail, es <b>muy probable</b> que tengan tus datos y te envíen SPAM.
            Al suscribirte, puedes marcarlas y les enviamos una <b>oposición al marketing formal</b>.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {cats.map((cat) => (
          <div key={cat}>
            <div className="eyebrow text-gray-500 mb-2">{cat}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {byCat[cat].slice(0, 10).map((e) => {
                const score = e.spam_score || 3;
                const scoreClass = score >= 5 ? "bg-red-100 text-red-800 border-red-300"
                  : score === 4 ? "bg-orange-100 text-orange-800 border-orange-300"
                  : score === 3 ? "bg-amber-100 text-amber-800 border-amber-300"
                  : "bg-lime-100 text-lime-800 border-lime-300";
                return (
                  <div
                    key={e.slug}
                    data-testid={`empresa-teaser-${e.slug}`}
                    className="bg-white border border-gray-200 p-3 relative"
                  >
                    <div className="font-semibold text-sm text-gray-900 truncate">{e.name}</div>
                    <div className={`mt-2 inline-flex items-center px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${scoreClass}`}>
                      SPAM {score >= 5 ? "AGRESIVO" : score === 4 ? "ALTO" : score === 3 ? "MEDIO" : "BAJO"}
                    </div>
                    <div className="mt-2 text-[10px] text-gray-500 italic leading-snug">
                      Posiblemente tiene tus datos
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

